import { takeLeading, put, call, select } from "redux-saga/effects";
import { TResult, TSubmittable } from "../../../../sg-core";
import {
    IRequestFetchCreateObservation,
    createSuccessFetchCreateObservationAction,
    createFailureFetchCreateObservationAction,
    requestCreateObservation,
} from "../actions/observationActions";

import IObservation, { INewObservation } from "../../interfaces/IObservation";
import { IObservationService } from "../../services/observationService";
import { selectNewObservation } from "../selectors/observationSelectors";
import { createSelectFormTypeById, createSelectFormDefinitionById } from "../selectors/formSelectors";
import IFormDefinition from "../../interfaces/IFormDefinition";

import IFormType from "../../interfaces/IFormType";
import { createRequestFetchObservationPageAction } from "../actions/observationListActions";
import { makeObservationPacakge } from "../../data/observation/useMakeObservationPackage";
import { selectCategoriesByIdState } from "../selectors/categorySelectors";
import ICategory from "../../interfaces/ICategory";
import { observationToSearchableText } from "../../utils/observationToSearchableText";
import { observationToPersonIds } from "@/@auditoinnit/auditoinnit/utils/data.helpers";
import { IPersonService } from "../../services/personService";
import IPerson from "../../interfaces/IPerson";

const makeFetchCreateObservationSagat = (
    observationService: IObservationService,
    personService: IPersonService,
) =>
    function* saga(_: IRequestFetchCreateObservation) {
        const newObservation: TSubmittable<INewObservation> = yield select(selectNewObservation);

        const anonymousSession = window.location.pathname.indexOf("anonyymi") > -1;

        // GET FORM TYPE
        const formType: IFormType | undefined = yield select(
            createSelectFormTypeById(newObservation.data.formType),
        );
        if (!formType) {
            yield put(createFailureFetchCreateObservationAction(new Error("Missing formType")));
            return;
        }

        // GET CATEGORY
        const categories: { [key: string]: ICategory } = yield select(selectCategoriesByIdState);
        const category: ICategory | undefined = categories[formType.category];
        if (!category) {
            yield put(createFailureFetchCreateObservationAction(new Error("Missing category")));
            return;
        }

        // GET FORM DEFINITION
        const formDefinition: IFormDefinition | undefined = yield select(
            createSelectFormDefinitionById(formType.formDefinition),
        );
        if (!formDefinition) {
            yield put(createFailureFetchCreateObservationAction(new Error("Missing form definition")));
            return;
        }

        // IMPORTANT STUFF HERE
        // JSON schema that configures form and observation view is stored inside formData,
        // to ensure viewing of saved data dosn't corrupt when schemas on server changes.
        // Schema versioning might replace this in future.
        newObservation.data.formSchema = formDefinition.schema;

        let personIds = observationToPersonIds(
            newObservation.data.formData as any,
            newObservation.data.formSchema,
        );


        if (anonymousSession) {
            newObservation.data.searchText = observationToSearchableText(
                newObservation.data.formData,
                newObservation.data.formSchema,
                [],
                [],
            );
        } else {
            const personResults: TResult<IPerson[]> = yield call(personService.getPersons, personIds);
            if (!personResults.isSuccess) {
                throw personResults.error;
            }

            newObservation.data.searchText = observationToSearchableText(
                newObservation.data.formData,
                newObservation.data.formSchema,
                personResults.value,
                [],
            );
        }

        try {
            const saveResult: TResult<{ observation: IObservation; event: { id: string } }> = yield call(
                observationService.createObservation,
                newObservation.data,
            );
            if (!saveResult.isSuccess) {
                throw saveResult.error;
            }

            const packageData = makeObservationPacakge({
                observation: saveResult.value.observation,
                category,
                formType,
            });

            const historyPackage: TResult<{ id: string }> = yield call(
                observationService.saveHistoryData,
                packageData,
            );
            if (!historyPackage.isSuccess) {
                throw historyPackage.error;
            }

            const eventsResult: TResult<{ id: string }> = yield call(
                observationService.updateHistoryEvent,
                saveResult.value.event.id,
                { historyPackageId: historyPackage.value.id },
            );
            if (!eventsResult.isSuccess) {
                throw eventsResult.error;
            }

            yield put(createRequestFetchObservationPageAction());
            yield put(createSuccessFetchCreateObservationAction(saveResult.value.observation));
        } catch (e) {
            console.log(e);
            yield put(createFailureFetchCreateObservationAction(e as Error));
        }
    };

export default function create(observationService: IObservationService, personService: IPersonService) {
    return takeLeading(
        requestCreateObservation,
        makeFetchCreateObservationSagat(observationService, personService),
    );
}
